import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import themeable from '@instructure/ui-themeable';
import { Tooltip } from '@instructure/ui-tooltip';
var styles = {
  componentId: 'cICqq',
  template: function template(theme) {
    return "\n\n.cICqq_EwaR{align-items:center;background-color:".concat(theme.backgroundColor || 'inherit', ";border:none;color:").concat(theme.color || 'inherit', ";cursor:pointer;display:flex;height:100%;justify-content:center;width:").concat(theme.width || 'inherit', "}\n\n.cICqq_EwaR:hover{background-color:").concat(theme.hoverColor || 'inherit', "}\n\n.cICqq_EwaR:active{background-color:").concat(theme.activeColor || 'inherit', "}\n\n.cICqq_EwaR:focus{border:0.0625rem solid ").concat(theme.activeColor || 'inherit', ";box-shadow:inset 0 0 0 0.125rem ").concat(theme.focusColor || 'inherit', ";outline:none}\n\n.cICqq_kuin{border-radius:0.25rem 0 0 0.25rem}\n\n.cICqq_ypmX{border-radius:0 0.25rem 0.25rem 0}\n\n.cICqq_dAWx{display:flex}\n\n.cICqq_scHz{height:100%}");
  },
  'button': 'cICqq_EwaR',
  'buttonStart': 'cICqq_kuin',
  'buttonEnd': 'cICqq_ypmX',
  'divWrapper': 'cICqq_dAWx',
  'tooltipWrapper': 'cICqq_scHz'
};
import theme from './theme';
/**
---
private: true
---
@module PlayerButton
**/

var PlayerButton = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(PlayerButton, _Component);

  function PlayerButton() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, PlayerButton);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(PlayerButton)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.handleKeyDown = function (e) {
      // prevent FF from emitting a keyboard event
      if (e.key === ' ' || e.key === 'Enter') {
        e.stopPropagation();
      }
    };

    _this.handleInsaneBrowsers = function (e) {
      /*
        On Mac OS X 10.X + (Safari or FF), buttons don't get focused when clicked
        ref: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Clicking_and_focus
         We want to make sure that experience is consistent throughout different browsers, so we're
        explicitly focusing the PlayerButtons on mouse down.
      */
      e.preventDefault(); // prevent focusing on the Player's container

      if (!_this.buttonRef) {
        return;
      }

      _this.buttonRef.focus();
    };

    _this.handleOnMouseDown = function (e) {
      _this.handleInsaneBrowsers(e);

      _this.props.onMouseDown(e);
    };

    _this.handleRef = function (e) {
      _this.buttonRef = e;

      _this.props.forwardRef(e);
    };

    _this.renderButton = function (_ref) {
      var _classNames;

      var videoId = _ref.videoId,
          onClick = _ref.onClick,
          children = _ref.children,
          position = _ref.position;
      var className = classNames((_classNames = {}, _defineProperty(_classNames, styles.button, true), _defineProperty(_classNames, styles.buttonStart, position === 'start'), _defineProperty(_classNames, styles.buttonEnd, position === 'end'), _classNames));
      return React.createElement("button", {
        className: className,
        onMouseDown: _this.handleOnMouseDown,
        onClick: onClick,
        onKeyDown: _this.handleKeyDown,
        "aria-controls": videoId,
        ref: _this.handleRef
      }, React.createElement("div", {
        className: styles.divWrapper
      }, children));
    };

    return _this;
  }

  _createClass(PlayerButton, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          tooltipLabel = _this$props.tooltipLabel,
          tooltipMountNode = _this$props.tooltipMountNode;

      if (tooltipLabel) {
        return React.createElement("div", {
          className: styles.tooltipWrapper
        }, React.createElement(Tooltip, {
          renderTip: tooltipLabel,
          mountNode: tooltipMountNode
        }, React.createElement("span", null, this.renderButton(this.props))));
      } else {
        return this.renderButton(this.props);
      }
    }
  }]);

  PlayerButton.displayName = "PlayerButton";
  return PlayerButton;
}(Component), _class2.propTypes = {
  /**
   * Id of the video element. Used to ensure
   * correct aria properties are applied.
   */
  videoId: PropTypes.string.isRequired,
  forwardRef: PropTypes.func,
  onClick: PropTypes.func,
  onMouseDown: PropTypes.func,
  position: PropTypes.oneOf(['start', 'middle', 'end']),
  tooltipLabel: Tooltip.propTypes.renderTip,
  tooltipMountNode: Tooltip.propTypes.mountNode,
  children: PropTypes.node
}, _class2.defaultProps = {
  forwardRef: function forwardRef(ref) {},
  onClick: function onClick(e) {},
  onMouseDown: function onMouseDown(e) {},
  position: 'middle',
  tooltipLabel: Tooltip.defaultProps.tooltipLabel,
  tooltipMountNode: Tooltip.defaultProps.mountNode
}, _temp)) || _class);
export { PlayerButton as default };